import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { PushPrompt } from './PushPrompt';
import { CommandPalette } from './CommandPalette';
import { ErrorBoundary } from './ErrorBoundary';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard, Package, CheckSquare, BarChart3, Archive, Users, LogOut, Menu, X, MessageSquare,
  RefreshCcw, Layers, Settings, FileText, XCircle, CheckCircle, Warehouse, Truck, Route, Wrench,
  ClipboardCheck, BellRing, FileBarChart2, Search, Bell, Sun, Moon, Monitor, PanelLeftClose,
  PanelLeftOpen, Plus, Repeat, LineChart, PackageSearch, Smartphone, Home, MoreHorizontal, ShoppingCart, MessageSquareWarning, Mail,
} from 'lucide-react';

// The CRM's frame: a grouped sidebar (collapsible to icons), a top bar with
// search (⌘K), quick add, notifications, theme and account, and the page.

const SECTIONS = [
  { title: 'Overview', items: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, area: 'dashboard' },
    { name: 'Reports', href: '/reports', icon: FileBarChart2, area: 'analytics' },
  ] },
  { title: 'Orders', items: [
    { name: 'Orders', href: '/orders', icon: Package, area: 'orders' },
    { name: 'Customer alerts', href: '/care-alerts', icon: BellRing, area: 'orders', badge: 'alerts' },
    { name: 'Cancelled', href: '/cancelled-orders', icon: XCircle, area: 'orders' },
    { name: 'Resolved', href: '/resolved-orders', icon: CheckCircle, area: 'orders' },
    { name: 'Abandoned carts', href: '/abandoned-carts', icon: ShoppingCart, area: 'orders' },
  ] },
  { title: 'After the sale', items: [
    { name: 'Returns', href: '/returns', icon: RefreshCcw, area: 'returns' },
    { name: 'Replacements', href: '/replacements', icon: Repeat, area: 'returns' },
    { name: 'Installations', href: '/installations', icon: Wrench, area: 'installations' },
    { name: 'Website claims', href: '/website-claims', icon: MessageSquareWarning, area: 'claims', badge: 'websiteClaims' },
    { name: 'Claims', href: '/claims', icon: FileText, area: 'claims' },
    { name: 'Amazon inbox', href: '/amazon-inbox', icon: Mail, area: 'claims' },
  ] },
  { title: 'Stock & shipping', items: [
    { name: 'Inventory', href: '/inventory', icon: Archive, area: 'inventory' },
    { name: 'Logistics', href: '/logistics', icon: Truck, area: 'logistics' },
    { name: 'Tracking', href: '/logistics/tracking', icon: Route, area: 'logistics', badge: 'courier' },
    { name: 'Dispatch readiness', href: '/logistics-readiness', icon: ClipboardCheck, area: 'logistics' },
    { name: 'Warehouses', href: '/warehouses', icon: Warehouse, area: 'logistics' },
  ] },
  { title: 'Insights', items: [
    { name: 'Analytics', href: '/analytics', icon: BarChart3, area: 'analytics' },
    { name: 'Advanced analytics', href: '/analytics/advanced', icon: LineChart, area: 'analytics' },
  ] },
  { title: 'Customers', items: [
    { name: 'WhatsApp', href: '/whatsapp', icon: MessageSquare, area: 'whatsapp' },
    { name: 'Tasks', href: '/tasks', icon: CheckSquare, area: 'tasks' },
  ] },
  { title: 'Admin', items: [
    { name: 'Channels', href: '/channels', icon: Layers, area: 'channels' },
    { name: 'Team', href: '/team', icon: Users, area: 'team' },
    { name: 'Settings', href: '/settings', icon: Settings, area: 'settings' },
  ] },
];

// The nav item whose address is the longest match for the current page
// (so /logistics/tracking lights Tracking, not Logistics).
const activeHref = (path) => {
  let best = '';
  SECTIONS.forEach((s) => s.items.forEach((i) => {
    if ((path === i.href || path.startsWith(`${i.href}/`)) && i.href.length > best.length) best = i.href;
  }));
  return best;
};
const isActive = (item, path) => item.href === activeHref(path);

// Theme: light, dark or follow the device; remembered on this browser.
const applyTheme = (mode) => {
  const dark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
};
const readTheme = () => { try { return localStorage.getItem('fv-theme') || 'light'; } catch (e) { return 'light'; } };

const PageSkeleton = () => (
  <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading">
    <div className="h-8 w-56 rounded-md bg-muted" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl bg-muted" />)}</div>
    <div className="h-80 rounded-xl bg-muted" />
  </div>
);

export const Layout = () => {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem('fv-sidebar') === 'collapsed'; } catch (e) { return false; } });
  const [theme, setTheme] = useState(readTheme);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [counts, setCounts] = useState({ alerts: 0, courier: 0, websiteClaims: 0 });

  const sections = useMemo(() => SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => can(i.area)) }))
    .filter((s) => s.items.length), [can]);
  const current = useMemo(() => sections.flatMap((s) => s.items.map((i) => ({ ...i, group: s.title })))
    .find((i) => isActive(i, location.pathname)), [sections, location.pathname]);

  useEffect(() => { applyTheme(theme); try { localStorage.setItem('fv-theme', theme); } catch (e) { /* private mode */ } }, [theme]);
  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => applyTheme('system');
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [theme]);
  useEffect(() => { try { localStorage.setItem('fv-sidebar', collapsed ? 'collapsed' : 'open'); } catch (e) { /* ignore */ } }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // ⌘K / Ctrl+K anywhere; "/" when not typing.
  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o); }
      else if (e.key === '/' && !typing) { e.preventDefault(); setPaletteOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Live counts for the bell and the sidebar badges, refreshed every minute.
  const loadCounts = useCallback(async () => {
    const next = { alerts: 0, courier: 0, websiteClaims: 0 };
    await Promise.all([
      can('orders') && api.get('/care-alerts', { params: { limit: 1 } })
        .then((r) => { next.alerts = Object.values(r.data.open_counts || {}).reduce((a, b) => a + b, 0); }).catch(() => {}),
      can('logistics') && api.get('/logistics/tracking/pending')
        .then((r) => { next.courier = (r.data.rows || []).length; }).catch(() => {}),
      can('claims') && api.get('/claims/website/count')
        .then((r) => { next.websiteClaims = r.data.new || 0; }).catch(() => {}),
    ]);
    setCounts(next);
  }, [can]);
  useEffect(() => {
    loadCounts();
    const t = setInterval(loadCounts, 60000);
    window.addEventListener('fv-counts-refresh', loadCounts);
    return () => { clearInterval(t); window.removeEventListener('fv-counts-refresh', loadCounts); };
  }, [loadCounts]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = (user?.name || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const total = counts.alerts + counts.courier + counts.websiteClaims;
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'system' ? Monitor : Sun;

  const Nav = ({ compact }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5" aria-label="Main">
      {sections.map((section) => (
        <div key={section.title}>
          {!compact && <p className="px-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">{section.title}</p>}
          <div className="space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item, location.pathname);
              const badge = item.badge ? counts[item.badge] : 0;
              return (
                <button key={item.href} type="button" title={compact ? item.name : undefined}
                  onClick={() => navigate(item.href)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`group relative w-full flex items-center gap-3 rounded-lg ${compact ? 'justify-center px-0 py-2.5' : 'px-3 py-2'} text-[13.5px] font-medium transition-colors ${
                    active ? 'bg-sidebar-hover text-white' : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white'}`}>
                  {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-sidebar-active" />}
                  <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-sidebar-active' : 'text-sidebar-muted group-hover:text-sidebar-foreground'}`} />
                  {!compact && <span className="truncate">{item.name}</span>}
                  {badge > 0 && (compact
                    ? <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-sidebar-active" />
                    : <span className="ml-auto rounded-full bg-sidebar-active px-1.5 py-px text-[10.5px] font-semibold text-white tabular-nums">{badge}</span>)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  const Brand = ({ compact }) => (
    <button type="button" onClick={() => navigate('/dashboard')} className={`flex items-center gap-2.5 ${compact ? 'justify-center w-full' : ''}`}>
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-sidebar-active text-white font-[Manrope] font-extrabold text-sm">F</span>
      {!compact && <span className="text-[15px] font-semibold tracking-tight text-white font-[Manrope]">Furniva <span className="text-sidebar-muted font-medium">CRM</span></span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 hidden lg:flex flex-col bg-sidebar transition-[width] duration-200 ${collapsed ? 'w-[68px]' : 'w-60'}`} data-testid="sidebar">
        <div className={`h-14 flex items-center ${collapsed ? 'px-2' : 'px-5'}`}><Brand compact={collapsed} /></div>
        <Nav compact={collapsed} />
        <div className="p-3 border-t border-white/5">
          <button type="button" onClick={() => setCollapsed((c) => !c)} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-[12.5px] text-sidebar-muted hover:bg-sidebar-hover hover:text-white ${collapsed ? 'justify-center px-0' : ''}`}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" />Collapse</>}
          </button>
        </div>
      </aside>

      {/* Phone / tablet sidebar */}
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 flex flex-col bg-sidebar transition-transform duration-200 lg:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-14 flex items-center justify-between px-5">
          <Brand />
          <button type="button" onClick={() => setMobileOpen(false)} className="text-sidebar-muted hover:text-white" data-testid="close-sidebar-button" aria-label="Close menu"><X className="h-5 w-5" /></button>
        </div>
        <Nav />
      </aside>

      <div className={`transition-[padding] duration-200 ${collapsed ? 'lg:pl-[68px]' : 'lg:pl-60'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <div className="h-full px-3 sm:px-4 lg:px-6 flex items-center gap-2 sm:gap-3">
            <button type="button" className="lg:hidden -ml-1 p-1.5 rounded-md hover:bg-muted" onClick={() => setMobileOpen(true)} data-testid="open-sidebar-button" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden md:flex items-center gap-1.5 text-sm min-w-0">
              {current && <>{current.group !== current.name && <><span className="text-muted-foreground">{current.group}</span><span className="text-muted-foreground/50">/</span></>}<span className="font-medium truncate">{current.name}</span></>}
            </div>

            <button type="button" onClick={() => setPaletteOpen(true)}
              className="ml-auto md:ml-6 flex flex-1 md:flex-none min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-3 h-9 text-sm text-muted-foreground hover:border-primary/40 md:w-80">
              <Search className="h-4 w-4" /><span className="truncate">Search orders, phone, AWB…</span>
              <kbd className="ml-auto hidden md:inline rounded border border-border bg-muted px-1.5 py-px text-[10.5px] font-medium">⌘K</kbd>
            </button>

            <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
              {can('orders', 'edit') && (
                <button type="button" onClick={() => navigate('/orders/new')} className="hidden sm:flex items-center gap-1.5 h-9 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                  <Plus className="h-4 w-4" />New order
                </button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="relative grid h-9 w-9 place-items-center rounded-lg hover:bg-muted" aria-label="Notifications">
                    <Bell className="h-[18px] w-[18px]" />
                    {total > 0 && <span className="absolute right-1 top-1 min-w-[16px] rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground tabular-nums">{total > 99 ? '99+' : total}</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel>Needs attention</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/care-alerts')} className="gap-2">
                    <BellRing className="h-4 w-4 text-red-600" /><span className="flex-1">Customer alerts</span><span className="tabular-nums font-semibold">{counts.alerts}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/website-claims')} className="gap-2">
                    <MessageSquareWarning className="h-4 w-4 text-amber-600" /><span className="flex-1">New website claims</span><span className="tabular-nums font-semibold">{counts.websiteClaims}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/logistics/tracking')} className="gap-2">
                    <PackageSearch className="h-4 w-4 text-sky-600" /><span className="flex-1">Courier updates to apply</span><span className="tabular-nums font-semibold">{counts.courier}</span>
                  </DropdownMenuItem>
                  {total === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">All clear.</p>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/notifications')} className="gap-2">
                    <Smartphone className="h-4 w-4 text-muted-foreground" /><span className="flex-1">Phone notifications…</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="grid h-9 w-9 place-items-center rounded-lg hover:bg-muted" aria-label="Theme"><ThemeIcon className="h-[18px] w-[18px]" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setTheme('light')} className="gap-2"><Sun className="h-4 w-4" />Light</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('dark')} className="gap-2"><Moon className="h-4 w-4" />Dark</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('system')} className="gap-2"><Monitor className="h-4 w-4" />Same as device</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="ml-1 grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-[12px] font-semibold text-primary" aria-label="Account">{initials}</button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="font-normal">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {can('settings') && <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2"><Settings className="h-4 w-4" />Settings</DropdownMenuItem>}
                  {can('team') && <DropdownMenuItem onClick={() => navigate('/team')} className="gap-2"><Users className="h-4 w-4" />Team</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600" data-testid="logout-button"><LogOut className="h-4 w-4" />Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="px-4 pt-6 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:px-8 lg:py-7 max-w-[1600px] mx-auto">
          {location.pathname !== '/notifications' && <PushPrompt />}
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      {/* Phone tab bar: the five places used most on the move. */}
      <nav className="lg:hidden fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]" aria-label="Quick">
        <div className="grid grid-cols-5">
          {[
            { href: '/dashboard', label: 'Home', icon: Home, show: true },
            { href: '/orders', label: 'Orders', icon: Package, show: can('orders') },
            { href: '/logistics/tracking', label: 'Tracking', icon: Route, show: can('logistics'), badge: counts.courier },
            { href: '/whatsapp', label: 'WhatsApp', icon: MessageSquare, show: can('whatsapp') },
          ].filter((t) => t.show).concat([{ href: null, label: 'More', icon: MoreHorizontal, badge: counts.alerts }]).slice(0, 5).map((t) => {
            const Icon = t.icon;
            const active = t.href && (location.pathname === t.href || (t.href !== '/dashboard' && location.pathname.startsWith(t.href)));
            return (
              <button key={t.label} type="button" onClick={() => (t.href ? navigate(t.href) : setMobileOpen(true))}
                className={`relative flex flex-col items-center justify-center gap-0.5 h-16 text-[11px] font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>
                <Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.25 : 1.75} />
                {t.label}
                {t.badge > 0 && <span className="absolute top-2 left-1/2 ml-2 min-w-[16px] rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground tabular-nums">{t.badge > 99 ? '99+' : t.badge}</span>}
                {active && <span className="absolute top-0 inset-x-6 h-0.5 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>
      </nav>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} sections={sections} />
    </div>
  );
};

export default Layout;
